import {Injectable} from '@angular/core';
import {
  AuditLogPage,
  AuditLogsService,
  CommissionFee, CommissionFeeGet,
  CommissionFeePeriodPricePost, CommissionFeePeriodPriceWithLevel,
  CommissionFeePost,
  CommissionFeePut,
  CommissionFeeService, CommissionLevel, Fee, FeePeriodPrice, FeePeriodPricePost, FeePost, FeePut, PaymentTerm, Period, ServiceFeeService
} from '../../generated/sales-terminal-client';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import moment from 'moment-timezone';
import {SortedCommissionFee} from '../../components/fees/commission-fees/commission-fees.component';
import {cloneDeep} from 'lodash-es';
import {PageableResult, PageableSearch} from '../../components/scroll-load-component/scroll-load.component';
import {SortedFee} from '../../components/fees/service-fees/service-fees.component';

export interface ServiceFeeFilter extends PageableSearch {
  name?: string;
  salespointIds?: number[];
  showInactive?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeeService {

  constructor(private commissionApi: CommissionFeeService,
              private serviceApi: ServiceFeeService,
              private snackService: SnackService,
              private auditLogsService: AuditLogsService
  ) {}

  // COMMISSION FEES
  public updateCommissionFee(id: number, commissionFeePut: CommissionFeePut): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeIdPut(id, commissionFeePut).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public addCommissionFeePeriodPrice(id: number, periodPricePost: CommissionFeePeriodPricePost): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeIdPeriodPricePost(id, periodPricePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateCommissionFeePeriodPrice(id: number, periodPriceId: number, periodPricePost: CommissionFeePeriodPricePost): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeIdPeriodPricePeriodPriceIdPut(id, periodPriceId, periodPricePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public removeCommissionFeePeriodPrice(id: number, periodPriceId: number): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeIdPeriodPricePeriodPriceIdDelete(id, periodPriceId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  // SuperCentre
  public getSuperCentreCommissionFees(): Observable<CommissionFeeGet[]> {
    return this.commissionApi.commissionFeeSuperCentreOverviewGet().pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public createSuperCentreCommissionFee(commissionFeePost: CommissionFeePost): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeSuperCentrePost(commissionFeePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  // Legal
  public getLegalPersonCommissionFees(legalPersonId: number): Observable<CommissionFeeGet[]> {
    return this.commissionApi.commissionFeeLegalpersonIdOverviewGet(legalPersonId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public createLegalPersonCommissionFee(legalPersonId: number, commissionFeePost: CommissionFeePost): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeLegalpersonIdPost(legalPersonId, commissionFeePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getLegalPersonCommissionFeePaymentTerm(legalPersonId: number): Observable<PaymentTerm> {
    return this.commissionApi.commissionFeeLegalpersonIdPaymentTermGet(legalPersonId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateLegalPersonCommissionFeePaymentTerm(legalPersonId: number, paymentTerm: PaymentTerm): Observable<PaymentTerm> {
    return this.commissionApi.commissionFeeLegalpersonIdPaymentTermPut(legalPersonId, paymentTerm).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  // Event
  public getEventCommissionFees(eventId: number): Observable<CommissionFeeGet[]> {
    return this.commissionApi.commissionFeeEventIdOverviewGet(eventId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public createEventCommissionFee(eventId: number, commissionFeePost: CommissionFeePost): Observable<CommissionFee> {
    return this.commissionApi.commissionFeeEventIdPost(eventId, commissionFeePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getEventCommissionFeePaymentTerm(eventId: number): Observable<PaymentTerm> {
    return this.commissionApi.commissionFeeEventIdPaymentTermGet(eventId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateEventCommissionFeePaymentTerm(eventId: number, paymentTerm: PaymentTerm): Observable<PaymentTerm> {
    return this.commissionApi.commissionFeeEventIdPaymentTermPut(eventId, paymentTerm).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public sortFeePeriods(commissionFee: CommissionFeeGet, targetLevel: CommissionLevel): SortedCommissionFee {
    const sortedFee: SortedCommissionFee = cloneDeep(commissionFee);
    sortedFee.currentPeriodPrice = commissionFee.activePeriodPrice;
    this.manipulatePeriodEnd(sortedFee.currentPeriodPrice, commissionFee.periodPrices);
    sortedFee.pastPeriodPrices = commissionFee.periodPrices.filter((periodPrice: CommissionFeePeriodPriceWithLevel) => this.isPeriodInPast(periodPrice.period));
    // Oldest fees first in Event view, newest first elsewhere.
    if (targetLevel !== CommissionLevel.Event) {
      sortedFee.pastPeriodPrices.sort((a: CommissionFeePeriodPriceWithLevel, b: CommissionFeePeriodPriceWithLevel) => new Date(b.period.start).getTime() - new Date(a.period.start).getTime());
    }
    sortedFee.pastPeriodPrices.forEach((periodPrice: CommissionFeePeriodPriceWithLevel) => this.manipulatePeriodEnd(periodPrice, commissionFee.periodPrices));
    sortedFee.futurePeriodPrices = commissionFee.periodPrices.filter((periodPrice: CommissionFeePeriodPriceWithLevel) => this.isPeriodInFuture(periodPrice.period));
    sortedFee.futurePeriodPrices.forEach((periodPrice: CommissionFeePeriodPriceWithLevel) => this.manipulatePeriodEnd(periodPrice, commissionFee.periodPrices));
    sortedFee.sameLevelPeriodPrices = commissionFee.periodPrices.filter((periodPrice: CommissionFeePeriodPriceWithLevel) => periodPrice.level === targetLevel);
    return sortedFee;
  }

  // SERVICE FEES
  public getServiceFees(filter: ServiceFeeFilter): Observable<PageableResult<Fee>> {
    return this.serviceApi.getLandCentreServiceFees(
      filter.name,
      filter.salespointIds,
      filter.showInactive,
      filter.page,
      filter.pageSize
    ).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  private manipulatePeriodEnd(periodPrice: CommissionFeePeriodPriceWithLevel, periodPrices: CommissionFeePeriodPriceWithLevel[]): void {
    if (periodPrice?.period?.end && periodPrices.some((otherPeriodPrice: CommissionFeePeriodPriceWithLevel) =>
      moment(otherPeriodPrice.period.start).isSame(moment(periodPrice.period.end)))) {
      periodPrice.period.end = moment(periodPrice.period.end).add(-1, 'minute').toISOString();
    }
  }

  public addServiceFee(feePost: FeePost): Observable<Fee> {
    return this.serviceApi.serviceFeePost(feePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateServiceFee(id: number, feePut: FeePut): Observable<Fee> {
    return this.serviceApi.serviceFeeIdPut(id, feePut).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public addServiceFeePeriod(feeId: number, periodPricePost: FeePeriodPricePost): Observable<Fee> {
    return this.serviceApi.serviceFeeIdPeriodPricePost(feeId, periodPricePost).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public updateServiceFeePeriod(feeId: number, periodPrice: FeePeriodPrice): Observable<Fee> {
    return this.serviceApi.serviceFeeServiceFeeIdPeriodPricePeriodPriceIdPut(feeId, periodPrice.id, periodPrice).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }
  public removeServiceFeePeriod(feeId: number, periodPriceId: number): Observable<Fee> {
    return this.serviceApi.serviceFeeServiceFeeIdPeriodPricePeriodPriceIdDelete(feeId, periodPriceId).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public getCommissionFeeSuperCentreAuditLogsByCommissionIds(feeIds: number[], page: number, pageSize: number): Observable<AuditLogPage> {
    return this.auditLogsService.getCommissionFeeSuperCentreAuditLogsByCommissionIds(feeIds, page, pageSize).pipe(
      tap({ error: (e) => this.snackService.showBackendError(e) })
    );
  }

  public sortServiceFeePeriods(serviceFee: SortedFee): SortedFee {
    const periodPriceSorter = (a: FeePeriodPrice, b: FeePeriodPrice) => new Date(a.period.start).getTime() - new Date(b.period.start).getTime();
    serviceFee.currentPeriodPrice = serviceFee.periodPrices.find((fee: FeePeriodPrice) => this.isPeriodActive(fee.period)) || null;
    serviceFee.pastPeriodPrices = serviceFee.periodPrices.filter((fee: FeePeriodPrice) => this.isPeriodInPast(fee.period));
    serviceFee.futurePeriodPrices = serviceFee.periodPrices.filter((fee: FeePeriodPrice) => this.isPeriodInFuture(fee.period));
    serviceFee.futurePeriodPrices.sort(periodPriceSorter);
    if (serviceFee.currentPeriodPrice) {
      serviceFee.currentAndFuturePeriodPrices = [serviceFee.currentPeriodPrice].concat(serviceFee.futurePeriodPrices);
    } else {
      serviceFee.currentAndFuturePeriodPrices = serviceFee.futurePeriodPrices;
    }
    serviceFee.pastPeriodPrices.sort(periodPriceSorter).reverse();
    serviceFee.currentAndFuturePeriodPrices.sort(periodPriceSorter);
    return serviceFee;
  }

  private isPeriodActive(period: Period): boolean {
    const today: moment.Moment = moment(new Date());
    return today.isSameOrAfter(moment(period?.start)) && (!period?.end || today.isSameOrBefore(moment(period?.end)));
  }
  private isPeriodInPast(period: Period): boolean {
    const today: moment.Moment = moment(new Date());
    return !!period.end && today.isAfter(moment(period?.end));
  }
  private isPeriodInFuture(period: Period): boolean {
    const today: moment.Moment = moment(new Date());
    return today.isBefore(moment(period?.start));
  }

}
